<template>
    <div class="bg-white">
        
        <div class="d-flex justify-content-between align-items-center mb-4">
            <h1 class="h4 mb-0">Update Profile</h1>
        </div>

         <Flash v-if="flashMessage" :alert="flashMessageType">{{ flashMessage }}</Flash>

        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>

        <form v-else-if="!errorMessage" @submit.prevent="handleSubmit">
            <!-- Personal Information Card -->
            <div class="card shadow-sm">
                <div class="card-body p-4">
                    <h5 class="mb-3">Personal Information</h5>
                    <div class="row g-3 mb-4">
                        <!-- First Name -->
                        <div class="col-md-6">
                            <label for="firstName" class="form-label">
                                First Name <span class="text-danger">*</span>
                            </label>
                            <div class="input-group">
                                <span class="input-group-text">
                                    <i class="bi bi-person"></i>
                                </span>
                                <input 
                                    type="text" 
                                    class="form-control" 
                                    :class="{ 'is-invalid': v$.firstName.$error }"
                                    id="firstName" 
                                    v-model="firstName"
                                    @blur="v$.firstName.$touch"
                                />
                                <div v-if="v$.firstName.$error" class="invalid-feedback">
                                    First name is required
                                </div>
                            </div>
                        </div>

                        <!-- Last Name -->
                        <div class="col-md-6">
                            <label for="lastName" class="form-label">
                                Last Name <span class="text-danger">*</span>
                            </label>
                            <div class="input-group">
                                <span class="input-group-text">
                                    <i class="bi bi-person"></i>
                                </span>
                                <input 
                                    type="text" 
                                    class="form-control" 
                                    :class="{ 'is-invalid': v$.lastName.$error }"
                                    id="lastName" 
                                    v-model="lastName"
                                    @blur="v$.lastName.$touch"
                                />
                                <div v-if="v$.lastName.$error" class="invalid-feedback">
                                    Last name is required
                                </div>
                            </div>
                        </div>

                        <!-- Role Selection -->
                        <div class="col-md-6">
                            <label for="connect_user_role_id" class="form-label">
                                Role <span class="text-danger">*</span>
                            </label>
                            <div class="input-group">
                                <span class="input-group-text">
                                    <i class="bi bi-briefcase"></i>
                                </span>
                                <select 
                                    class="form-select" 
                                    :class="{ 'is-invalid': v$.connect_user_role_id.$error }"
                                    id="connect_user_role_id" 
                                    v-model="connect_user_role_id"
                                    @blur="v$.connect_user_role_id.$touch"
                                >
                                    <option value="" disabled>Select a role</option>
                                    <option v-for="role of roles" :key="role.id" :value="role.id">
                                        {{ role.label }}
                                    </option>
                                </select>
                                <div v-if="v$.connect_user_role_id.$error" class="invalid-feedback">
                                    Please select a role
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Dentist Contact Information Card -->
            <div class="card shadow-sm mt-3" v-if="showDentistField">
                <div class="card-body p-4">
                    <h5 class="mb-3">Dentist Contact Information</h5>
                    <div class="row g-3 mb-4">
                        <!-- Dentist Selection -->
                        <div class="col-md-6">
                            <label for="dentistId" class="form-label">
                                Select Dentist <span class="text-danger">*</span>
                            </label>
                            <div class="input-group">
                                <span class="input-group-text">
                                    <i class="bi bi-person-badge"></i>
                                </span>
                                <select 
                                    class="form-select" 
                                    :class="{ 'is-invalid': v$.dentistId.$error }"
                                    id="dentistId" 
                                    v-model="dentistId"
                                    @blur="v$.dentistId.$touch"
                                >
                                    <option value="" disabled>Select a dentist</option>
                                    <option v-for="dentist in dentists" :key="dentist.id" :value="dentist.id">
                                        {{ dentist.name }}
                                    </option>
                                </select>
                                <div v-if="v$.dentistId.$error" class="invalid-feedback">
                                    Please select a dentist
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="row g-3 mb-4">
                        <!-- Phone -->
                        <div class="col-md-6">
                            <PhoneInput
                                v-model:phone-number="dentistPhone"
                                v-model:country-code="currentPhoneCode"
                                :class="{ 'is-invalid': v$.dentistPhone.$error }"
                                label="Mobile Number"
                                placeholder="07234 123 456"
                                required
                                @blur="v$.dentistPhone.$touch"
                                ref="phoneInput"
                            />
                            <div v-if="v$.dentistPhone.$error" class="invalid-feedback d-block">
                                Please enter a valid phone number
                            </div>
                        </div>

                        <!-- Email -->
                        <div class="col-md-6">
                            <label for="dentistEmail" class="form-label">Email address</label>
                            <div class="input-group">
                                <span class="input-group-text">
                                    <i class="bi bi-envelope"></i>
                                </span>
                                <input 
                                    type="email" 
                                    class="form-control" 
                                    :class="{ 'is-invalid': v$.dentistEmail.$error }"
                                    id="dentistEmail" 
                                    v-model="dentistEmail"
                                    placeholder="email@example.com"
                                    @blur="v$.dentistEmail.$touch"
                                />
                                <div v-if="v$.dentistEmail.$error" class="invalid-feedback">
                                    Please enter a valid email address
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Preferences Card (Dentist only) -->
            <div class="card shadow-sm mt-3" v-if="isDentist">
                <div class="card-body p-4">
                    <h5 class="mb-3">Preferences</h5>
                    <div class="row g-3 mb-4">
                        <!-- Allow promotional messages -->
                        <div class="col-md-6">
                            <label for="allow_promotional_content" class="form-label">
                                Allow marketing emails <span class="text-danger">*</span>
                            </label>
                            <div class="input-group">
                                <span class="input-group-text">
                                    <i class="bi bi-megaphone"></i>
                                </span>
                                <select
                                    class="form-select"
                                    :class="{ 'is-invalid': v$.allow_promotional_content.$error }"
                                    id="allow_promotional_content"
                                    v-model="allow_promotional_content"
                                    @blur="v$.allow_promotional_content.$touch"
                                >
                                    <option :value="2">No preference</option>
                                    <option :value="1">Yes</option>
                                    <option :value="0">No</option>
                                </select>
                                <div v-if="v$.allow_promotional_content.$error" class="invalid-feedback">
                                    Please select a valid option
                                </div>
                            </div>
                        </div>

                        <!-- Daily summary email preference -->
                        <div class="col-md-6">
                            <label for="is_daily_summary" class="form-label">
                                Receive daily summary email <span class="text-danger">*</span>
                            </label>
                            <div class="input-group">
                                <span class="input-group-text">
                                    <i class="bi bi-envelope"></i>
                                </span>
                                <select
                                    class="form-select"
                                    :class="{ 'is-invalid': v$.is_daily_summary.$error }"
                                    id="is_daily_summary"
                                    v-model="is_daily_summary"
                                    @blur="v$.is_daily_summary.$touch"
                                >
                                    <option :value="1">Yes</option>
                                    <option :value="0">No</option>
                                </select>
                                <div v-if="v$.is_daily_summary.$error" class="invalid-feedback">
                                    Please select an option
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <DropdownInput
                                label="Hold Scanner Orders"
                                v-model="hold_time"
                                :v="hold_time"
                                :options="holdTimes"
                                remove-empty-option="true"
                                prepend-icon="bi bi-pause-btn"
                                 />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Account Information Card -->
            <div class="card shadow-sm mt-3">
                <div class="card-body p-4">
                    <h5 class="mb-3">Account Information</h5>
                    <div class="row g-3 mb-4">
                        <div class="col-md-6">
                            <label class="form-label">Connect Email</label>
                            <div class="input-group">
                                <span class="input-group-text">
                                    <i class="bi bi-envelope"></i>
                                </span>
                                <input 
                                    type="text" 
                                    class="form-control bg-light" 
                                    :value="userEmail"
                                    disabled
                                />
                            </div>
                            <div class="form-text text-muted">The email you signed up with</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Submit Button -->
            <div class="mt-4 mb-4">
                <button 
                    type="submit" 
                    class="btn bg-primary"
                    :disabled="isSubmitting"
                >
                    <span v-if="isSubmitting">
                        <span class="spinner-border spinner-border-sm me-2" connect_user_role_id="status"></span>
                        Saving...
                    </span>
                    <span v-else>Save Changes</span>
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import api from "@/services/api";
import { useVuelidate } from '@vuelidate/core';
import Flash from "@/components/Flash.vue";
import PhoneInput from "@/components/PhoneInput.vue";
import { required, email, minLength, helpers } from '@vuelidate/validators';
import { getErrorMessage } from '@/utils/errorHandler';
import DropdownInput from "@/components/DropdownInput.vue";

export default {
    name: 'UpdateProfile',
    components: {
        Flash,
        PhoneInput,
        DropdownInput,
    },
    setup() {
        return { v$: useVuelidate() };
    },
    data() {
        return {
            loading: false,
            isSubmitting: false,
            errorMessage: false,
            flashMessage: '',
            flashMessageType: 'success',
            userEmail: '',
            firstName: '',
            lastName: '',
            connect_user_role_id: '',
            dentistId: '',
            dentistPhone: '',
            dentistEmail: '',
            allow_promotional_content: 2,
            is_daily_summary: 1,
            dentists: [],
            currentPhoneCode: '',
            roles: [],
            showDentistField: false,
            hold_time: 0,
            holdTimes: [],
        };
    },
    validations() {
        const rules = {
            firstName: { required, minLength: minLength(2) },
            lastName: { required, minLength: minLength(2) },
            connect_user_role_id: { required }
        };

        if (this.isDentist) {
            const phoneRegex = helpers.regex(/^[\d\s]+$/);
            const validatePhoneLength = helpers.withMessage(
                'Phone number must be 10 digits, or 11 digits if starting with 0',
                (value) => {
                    const digitsOnly = value.replace(/\s/g, '');
                    if (digitsOnly.startsWith('0')) {
                        return digitsOnly.length >= 10;
                    } else {
                        return digitsOnly.length >= 9;
                    }
                }
            );
            rules.dentistId = { required };
            rules.dentistPhone = { required, phoneRegex, validatePhoneLength };
            rules.dentistEmail = { required, email };
            rules.allow_promotional_content = { required };
            rules.is_daily_summary = { required };
        }

        return rules;
    },
    computed: {
        isDentist() {
            const role = this.roles.find((role) => role.id == this.connect_user_role_id);
            return role?.is_dentist || false;
        }
    },
    watch: {
        dentistId(newDentistId) {
            if (!newDentistId) {
                this.dentistPhone = '';
                this.dentistEmail = '';
                this.allow_promotional_content = 2;
                this.is_daily_summary = 1;
                return;
            }
            const selectedDentist = this.dentists.find(dentist => dentist.id == newDentistId);
            if (!selectedDentist) {
                return;
            }
            
            // Parse and set phone number values using PhoneInput component
            if (selectedDentist.phone) {
                this.$nextTick(() => {
                    if (this.$refs.phoneInput) {
                        const phoneData = this.$refs.phoneInput.parseInternationalPhone(selectedDentist.phone);
                        this.dentistPhone = phoneData.localNumber;
                        this.currentPhoneCode = phoneData.countryCode || selectedDentist.country_code || '';
                    } else {
                        // Fallback if component not ready
                        this.dentistPhone = selectedDentist.phone;
                        this.currentPhoneCode = selectedDentist.country_code || '';
                    }
                });
            } else {
                this.dentistPhone = '';
            }
            
            this.dentistEmail = selectedDentist.email;
            this.allow_promotional_content = selectedDentist.allow_promotional_content || 2;
            this.is_daily_summary = selectedDentist.is_daily_summary;
        },
        connect_user_role_id(newRoleId, oldRoleId) {
            const role = this.roles.find((role) => role.id == newRoleId);
            this.showDentistField = role?.is_dentist;

            if (this.showDentistField) {
                return;
            }

            // Only clear fields if this is a user-initiated change, not initial loading
            if (oldRoleId !== undefined && oldRoleId !== '') {
                this.dentistId = '';
                this.dentistPhone = '';
                this.dentistEmail = '';
                this.allow_promotional_content = '';
            }
        },
    },
    methods: {
        async fetchUserProfile() {
            this.loading = true;

            try {
                const response = await api.get('/api/v1/users/me');
                const userData = response.data.data;
                
                this.userEmail = userData.email || '';
                this.firstName = userData.first_name || '';
                this.lastName = userData.last_name || '';
                this.connect_user_role_id = String(userData.connect_user_role_id || '');

                if (userData.dentist) {
                    this.dentistId = userData.dentist.id || '';
                    this.dentistEmail = userData.dentist.email || '';
                    this.allow_promotional_content = userData.dentist.allow_promotional_content || 2;
                    this.is_daily_summary = userData.dentist.is_daily_summary;
                    
                    // Parse and set phone number values using PhoneInput component
                    if (userData.dentist.phone) {
                        this.$nextTick(() => {
                            if (this.$refs.phoneInput) {
                                const phoneData = this.$refs.phoneInput.parseInternationalPhone(userData.dentist.phone);
                                this.dentistPhone = phoneData.localNumber;
                                this.currentPhoneCode = phoneData.countryCode || userData.dentist.country_code || '';
                            } else {
                                // Fallback if component not ready
                                this.dentistPhone = userData.dentist.phone;
                                this.currentPhoneCode = userData.dentist.country_code || '';
                            }
                        });
                    } else {
                        this.dentistPhone = '';
                    }
                }
                
                await this.$nextTick();
                this.v$.$reset();
            } catch (error) {
                console.error('Failed to fetch user profile:', error);
                this.flashMessage = this.handleError(error, 'Failed to load profile. Please try again.');
            } finally {
                this.loading = false;
            }
        },

        async fetchRoles() {
            this.loading = true;

            try {
                const response = await api.get('/api/v1/roles');
                const roleData = response.data.roles;
                this.roles = roleData;
            } catch (error) {
                console.error('Failed to get all roles', error);
                this.flashMessage = this.handleError(error, 'Failed to load roles. Please try again.');
            }
            this.loading = false;
        },

        async fetchDentists() {
            try {
                const response = await api.get('/api/v1/accounts', { params: { practice_id: this.$store.state.currentPracticeId } });
                const data = response.data.data;

                const dentistMap = new Map();
                
                data
                    .filter(account => account.dentist)
                    .forEach(account => {
                        const dentistId = account.dentist.id;
                        // Only add if we haven't seen this dentist ID before
                        if (!dentistMap.has(dentistId)) {
                            dentistMap.set(dentistId, {
                                id: String(dentistId),
                                name: `${account.dentist.title || ''} ${account.dentist.first_name || ''} ${account.dentist.surname || ''}`.trim(),
                                phone: account.dentist.phone || '',
                                email: account.dentist.email || '',
                                allow_promotional_content: account.dentist.allow_promotional_content || null,
                                is_daily_summary: account.dentist.is_daily_summary,
                                country_code: account.dentist.country_code || '',
                                hold_time: account.dentist.draft_order_hold_time || 0,
                            });
                        }
                    });

                this.dentists = Array.from(dentistMap.values());

                if (this.dentists.length == 1) {
                    this.hold_time = this.dentists[0].hold_time;
                }

            } catch (error) {
                console.error('Failed to fetch dentists:', error);
                this.flashMessage = this.handleError(error, 'Failed to load dentists. Please try again');
            }
        },
        async handleSubmit() {
            // Validate form
            await this.v$.$validate();
            
            if (this.v$.$invalid) {
                this.scrollToFirstError();
                return;
            }
            
            await this.updateProfile();
        },
        
        async updateProfile() {
            this.isSubmitting = true;

            const payload = {
                first_name: this.firstName.trim(),
                last_name: this.lastName.trim(),
                connect_user_role_id: this.connect_user_role_id
            };

            const role = this.roles.find((role) => role.id == this.connect_user_role_id);

            if (role?.is_dentist) {
                payload.dentist_id = this.dentistId;
                
                // Get formatted phone from the PhoneInput component
                const formattedPhone = this.$refs.phoneInput ? 
                    this.$refs.phoneInput.getFormattedPhoneForSubmission() : 
                    this.dentistPhone.trim();
                
                payload.dentist = {
                    phone: formattedPhone,
                    email: this.dentistEmail.trim(),
                    allow_promotional_content: this.allow_promotional_content,
                    is_daily_summary: this.is_daily_summary,
                    country_code: `+${this.currentPhoneCode}`,
                    hold_time: this.hold_time,
                };
            }

            try {
                await api.patch('/api/v1/users/me', payload);

                await this.$store.dispatch('fetchUser');

                this.flashMessage = 'Your profile has been updated';
                
                this.scrollToTop();

            } catch (error) {
                console.error('Failed to update profile:', error);
                this.flashMessage = this.handleError(error, 'Failed to update profile. Please try again.');
                this.scrollToTop();
            } finally {
                this.isSubmitting = false;
            }
        },
        
        handleError(error, defaultMessage) {
            this.flashMessageType = 'danger';
            this.errorMessage = true;
            return getErrorMessage(error, defaultMessage);
        },
        
        scrollToFirstError() {
            this.$nextTick(() => {
                const firstError = document.querySelector('.is-invalid');
                if (firstError) {
                    firstError.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'center' 
                    });
                    firstError.focus();
                }
            });
        },
        
        checkDentistField() {
            const role = this.roles.find((role) => role.id == this.connect_user_role_id);
            this.showDentistField = role?.is_dentist;
        },
        
        scrollToTop() {
            window.scrollTo({ 
                top: 0, 
                behavior: 'smooth' 
            });
        },

        setHoldTimes: function() {
                let holdTimes = [];

                holdTimes.push({
                    id: 0,
                    label: 'Do Not Hold'
                });

                holdTimes.push({
                    id: 999,
                    label: 'Indefinitely'
                });

                for (let i = 1; i <= 48; i++) {
                    holdTimes.push({
                        id: i,
                        label: i === 1 ? '1 hour' : `${i} hours`
                    });
                }
                this.holdTimes = holdTimes;
            },
        
    },
    async mounted() {
        this.setHoldTimes();
        await Promise.all([
            this.fetchUserProfile(),
            this.fetchRoles(),
            this.fetchDentists()
        ]);
        this.checkDentistField();        
    }
};
</script>

<style scoped lang="scss">
.card {
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
    transition: box-shadow 0.2s ease-in-out;

    &:hover {
        box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    }
}

.form-control,
.form-select {
    &:disabled {
        cursor: not-allowed;
        background-color: #f8f9fa;
    }
    
    &:focus {
        border-color: #86b7fe;
        box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
    }
}

.bg-light {
    background-color: #f8f9fa !important;
}

.btn-primary {
    background-color: #0d6efd;
    border-color: #0d6efd;
    padding: 0.5rem 1.5rem;
    font-weight: 500;
    
    &:hover:not(:disabled) {
        background-color: #0b5ed7;
        border-color: #0a58ca;
    }
    
    &:disabled {
        opacity: 0.65;
        cursor: not-allowed;
    }
}

.alert {
    border-radius: 0.375rem;
    animation: slideDown 0.3s ease-in-out;
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

h5 {
    color: #212529;
    font-weight: 600;
}

.form-label {
    font-weight: 500;
    color: #495057;
    margin-bottom: 0.5rem;
}

.form-text {
    font-size: 0.875rem;
    margin-top: 0.25rem;
}

.invalid-feedback {
    font-size: 0.875em;
    margin-top: 0.25rem;
    animation: fadeIn 0.2s ease-in-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

.spinner-border-sm {
    width: 1rem;
    height: 1rem;
    border-width: 0.15em;
}
</style>
